import { useId, useState, useMemo } from 'react';
import { X, TrendingUp, Check } from 'lucide-react';
import { useT } from '../../i18n';
import { errMsg } from '../../lib/errors';
import {
  calcSingleMatchWinner,
  calcSingleMatchEloChange,
  ELO_K_FACTOR_DEFAULT,
} from '../../engine';
import { Dialog, EloWeightSlider } from '../ui';
import { safeId } from '../ui/labelledControl';
import { PlayerPicker } from './PlayerPicker';

export interface AddSingleMatchModalProps {
  /** All players available to pick from (id, name, elo, …). */
  allPlayers: any[];
  /** When set, the modal opens in edit mode pre-filled from this match. */
  initialMatch?: any;
  /** Closes the modal without saving. */
  onClose: () => void;
  /** Persists the (new or edited) single match; awaited while the spinner shows. */
  onSubmit: (match: any) => Promise<void> | void;
  /**
   * Optional: persists a brand-new player created inline from the search field
   * to the global player DB. When omitted, the "create new player" affordance
   * is hidden.
   */
  onPersistNewPlayer?: ((player: any) => Promise<void> | void) | null;
  /** Optional heading override (e.g. "Registrer resultat" when completing a planned match). */
  title?: string;
  /** Optional submit-button label override. */
  submitLabel?: string;
}

/**
 * Single-match registration/edit modal (player picker, padel-rule score grid,
 * ELO-weight slider, live ELO preview); extracted verbatim from the monolith
 * (legacy/App.jsx). Behaviour and markup are unchanged.
 *
 * Engine wiring swapped to named imports from '../../engine':
 *   Padel24Engine.singleMatch.calcWinner    → calcSingleMatchWinner
 *   Padel24Engine.singleMatch.calcEloChange → calcSingleMatchEloChange
 * and the monolith's module-local `ELO_K_FACTOR` (= ELO_K_FACTOR_DEFAULT) is
 * imported directly as ELO_K_FACTOR_DEFAULT — same value, byte-different name.
 */
export function AddSingleMatchModal({
  allPlayers,
  initialMatch,
  onClose,
  onSubmit,
  onPersistNewPlayer = null,
  title,
  submitLabel,
}: AddSingleMatchModalProps) {
  const isEditing = !!initialMatch;
  const t = useT();
  const uid = safeId('single-match', useId());
  const ids = {
    title: `${uid}-title`,
    sub: `${uid}-sub`,
    result: `${uid}-result`,
    when: `${uid}-when`,
  };

  // Init state from initialMatch if we are editing, otherwise empty values
  const [team1, setTeam1] = useState(() =>
    initialMatch
      ? { p1Id: initialMatch.team1.p1Id, p2Id: initialMatch.team1.p2Id }
      : { p1Id: null, p2Id: null },
  );
  const [team2, setTeam2] = useState(() =>
    initialMatch
      ? { p1Id: initialMatch.team2.p1Id, p2Id: initialMatch.team2.p2Id }
      : { p1Id: null, p2Id: null },
  );
  const [sets, setSets] = useState(() => {
    if (initialMatch && Array.isArray(initialMatch.sets)) {
      const init = initialMatch.sets.map((s) => ({ games1: s.games1, games2: s.games2 }));
      while (init.length < 3) init.push({ games1: null, games2: null });
      return init.slice(0, 3);
    }
    return [
      { games1: null, games2: null },
      { games1: null, games2: null },
      { games1: null, games2: null },
    ];
  });
  // Which score field has an open selection menu (e.g. "0-1" for set 0, side 1)
  const [activeScore, setActiveScore] = useState(null);

  // Date and time — from initialMatch if we are editing, otherwise now
  const initDate = initialMatch ? new Date(initialMatch.playedAt) : new Date();
  const [date, setDate] = useState(() => {
    const y = initDate.getFullYear();
    const m = String(initDate.getMonth() + 1).padStart(2, '0');
    const d = String(initDate.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  });
  const [time, setTime] = useState(
    String(initDate.getHours()).padStart(2, '0') +
      ':' +
      String(initDate.getMinutes()).padStart(2, '0'),
  );

  const [submitting, setSubmitting] = useState(false);

  // ELO weight for this single match (0-200%). New match: default 200% (full best of 3).
  // Editing: read from the match's stored eloWeightPct, otherwise derive from kFactor, otherwise 100%.
  const [eloWeightPct, setEloWeightPct] = useState(() => {
    if (initialMatch) {
      if (typeof initialMatch.eloWeightPct === 'number') return initialMatch.eloWeightPct;
      if (typeof initialMatch.kFactor === 'number')
        return Math.round((initialMatch.kFactor / ELO_K_FACTOR_DEFAULT) * 100);
      return 100;
    }
    return 200;
  });
  // kFactor derived from the chosen weight — used in preview and saving.
  const eloKFactor = (eloWeightPct / 100) * ELO_K_FACTOR_DEFAULT;

  const playerById = useMemo(() => {
    const map = {};
    allPlayers.forEach((p) => {
      map[p.id] = p;
    });
    return map;
  }, [allPlayers]);

  const selectedIds = [team1.p1Id, team1.p2Id, team2.p1Id, team2.p2Id].filter(Boolean);
  const allSelected = selectedIds.length === 4;

  // Valid sets — only those that have both numbers filled in
  const validSets = useMemo(() => {
    return sets
      .filter((s) => s.games1 !== null && s.games2 !== null)
      .map((s) => ({ games1: s.games1, games2: s.games2 }));
  }, [sets]);

  const winner = validSets.length >= 2 ? calcSingleMatchWinner(validSets) : null;
  const canSubmit = allSelected && winner !== null && date && time;

  // Preview ELO change
  const preview = useMemo(() => {
    if (!canSubmit) return null;
    const match = {
      team1: { p1Id: team1.p1Id, p2Id: team1.p2Id },
      team2: { p1Id: team2.p1Id, p2Id: team2.p2Id },
      sets: validSets,
      kFactor: eloKFactor, // preview with the chosen ELO weight (practice match = 0)
    };
    const calc = calcSingleMatchEloChange(allPlayers, match);
    return calc.error ? null : calc;
  }, [canSubmit, team1, team2, validSets, allPlayers, eloKFactor]);

  const handleSubmit = async () => {
    console.log(
      '[Padel24] handleSubmit kalt. canSubmit:',
      canSubmit,
      'team1:',
      team1,
      'team2:',
      team2,
      'validSets:',
      validSets,
    );
    if (!canSubmit) {
      console.warn('[Padel24] handleSubmit avbrutt — canSubmit er false');
      return;
    }
    setSubmitting(true);
    try {
      // Build playedAt from date + time
      const [year, month, day] = date.split('-').map(Number);
      const [hours, minutes] = time.split(':').map(Number);
      const playedAt = new Date(year, month - 1, day, hours, minutes).getTime();
      console.log('[Padel24] Sender til onSubmit. playedAt:', new Date(playedAt).toISOString());
      await onSubmit({
        team1: { p1Id: team1.p1Id, p2Id: team1.p2Id },
        team2: { p1Id: team2.p1Id, p2Id: team2.p2Id },
        sets: validSets,
        format: 'best_of_3',
        kFactor: eloKFactor, // ELO weight chosen in the modal (0 = practice match)
        eloWeightPct, // stored for display/editing
        playedAt,
      });
      console.log('[Padel24] onSubmit ferdig');
    } catch (e) {
      console.error('[Padel24] handleSubmit feilet:', e);
      alert('Feil ved lagring: ' + errMsg(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    // A `Dialog` (#900): named by its title, described by «Best av 3 sett ·
    // Påvirker ELO», Tab kept inside, Escape closes as the backdrop does, focus
    // given back. The player picker opens its own Dialog inside this one; it is
    // the innermost while it is up, so Escape there closes the picker only.
    <Dialog
      labelledBy={ids.title}
      describedBy={ids.sub}
      onClose={onClose}
      showClose={false}
      backdropClassName="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40"
      className="card p-6 max-w-xl w-full max-h-[90vh] overflow-y-auto"
    >
      <div className="flex items-start justify-between mb-4">
        <div>
          <h2 id={ids.title} className="font-display text-xl font-semibold leading-tight">
            {title || (isEditing ? 'Endre kamp' : 'Logg ny kamp')}
          </h2>
          <p id={ids.sub} className="text-xs text-ink-3 font-mono">
            Best av 3 sett · Påvirker ELO
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label={t('ui.dialog.close')}
          className="p-1 text-ink-3 hover:text-ink-2"
        >
          <X size={18} />
        </button>
      </div>

      {/* Team setup + player picker (shared component) */}
      <div className="mb-4">
        <PlayerPicker
          allPlayers={allPlayers}
          team1={team1}
          team2={team2}
          onChangeTeam1={setTeam1}
          onChangeTeam2={setTeam2}
          onPersistNewPlayer={onPersistNewPlayer}
        />
      </div>

      {/* Result — clickable fields with dropdown choices 0-7 */}
      <div className="mb-4">
        <div
          id={ids.result}
          className="font-mono text-[10px] uppercase tracking-wider text-ink-3 mb-2 px-1"
        >
          Resultat (best av 3)
        </div>
        {/* A title over several controls is a GROUP (#900), not a lone label. */}
        <div role="group" aria-labelledby={ids.result} className="grid grid-cols-3 gap-2">
          {sets.map((s, i) => {
            // Count set winners BEFORE this set
            let t1Sets = 0,
              t2Sets = 0;
            for (let j = 0; j < i; j++) {
              const prev = sets[j];
              if (prev.games1 !== null && prev.games2 !== null) {
                if (prev.games1 > prev.games2) t1Sets++;
                else if (prev.games2 > prev.games1) t2Sets++;
              }
            }
            // If one team has won 2 sets, the match is decided
            const matchDecided = t1Sets >= 2 || t2Sets >= 2;

            return (
              <div
                key={i}
                className={`flex items-center gap-1.5 border rounded-lg px-2 py-2 relative ${
                  matchDecided
                    ? 'bg-bg-soft/20 border-line/50 opacity-50'
                    : 'bg-bg-soft/40 border-line'
                }`}
              >
                <span className="font-mono text-[10px] text-ink-3 w-4 flex-shrink-0">{i + 1}.</span>

                {/* Score 1 */}
                <button
                  type="button"
                  disabled={matchDecided}
                  aria-expanded={activeScore === `${i}-1`}
                  onClick={() =>
                    !matchDecided && setActiveScore(activeScore === `${i}-1` ? null : `${i}-1`)
                  }
                  className={`flex items-center justify-center font-mono text-sm font-semibold rounded transition-all ${
                    matchDecided
                      ? 'bg-paper border border-line-strong text-ink-3 cursor-not-allowed'
                      : activeScore === `${i}-1`
                        ? 'bg-lime-deep text-ink'
                        : s.games1 !== null
                          ? 'bg-paper border border-line-strong hover:border-ink-3'
                          : 'bg-paper border border-dashed border-ink-3 hover:border-ink text-ink-3'
                  }`}
                  style={{ width: 40, height: 32 }}
                >
                  {s.games1 !== null ? s.games1 : '–'}
                </button>

                <span className="text-ink-3 text-sm font-mono">:</span>

                {/* Score 2 */}
                <button
                  type="button"
                  disabled={matchDecided}
                  aria-expanded={activeScore === `${i}-2`}
                  onClick={() =>
                    !matchDecided && setActiveScore(activeScore === `${i}-2` ? null : `${i}-2`)
                  }
                  className={`flex items-center justify-center font-mono text-sm font-semibold rounded transition-all ${
                    matchDecided
                      ? 'bg-paper border border-line-strong text-ink-3 cursor-not-allowed'
                      : activeScore === `${i}-2`
                        ? 'bg-lime-deep text-ink'
                        : s.games2 !== null
                          ? 'bg-paper border border-line-strong hover:border-ink-3'
                          : 'bg-paper border border-dashed border-ink-3 hover:border-ink text-ink-3'
                  }`}
                  style={{ width: 40, height: 32 }}
                >
                  {s.games2 !== null ? s.games2 : '–'}
                </button>

                {/* Dropdown with valid numbers based on padel rules */}
                {(activeScore === `${i}-1` || activeScore === `${i}-2`) &&
                  (() => {
                    const isSide1 = activeScore.endsWith('-1');
                    const otherSideValue = isSide1 ? s.games2 : s.games1;

                    // Compute auto-fill for the opponent based on the rule:
                    // - Winner must have 6 (with a 2+ lead) or 7 (against 5 or 6)
                    // - Possible: 6-0, 6-1, 6-2, 6-3, 6-4, 7-5, 7-6 (and the reverse)
                    const getAutoFillForOther = (myScore) => {
                      if (myScore >= 0 && myScore <= 4) return 6; // opponent wins 6-X
                      if (myScore === 5) return 7; // opponent wins 7-5
                      if (myScore === 6) return null; // ambiguous: could be 6-7 or 7-6 (we wait)
                      if (myScore === 7) return null; // 7 means I win, opponent must be 5 or 6
                      return null;
                    };

                    // Which numbers are VALID for this side, given the opponent value?
                    const isValidGiven = (myScore, otherScore) => {
                      if (otherScore === null || otherScore === undefined) return true; // no constraint
                      // Check whether the combination myScore-otherScore is a valid padel result
                      const high = Math.max(myScore, otherScore);
                      const low = Math.min(myScore, otherScore);
                      // 6-0 to 6-4: high=6, low=0-4
                      if (high === 6 && low <= 4) return true;
                      // 7-5 or 7-6: high=7, low=5 or 6
                      if (high === 7 && (low === 5 || low === 6)) return true;
                      return false;
                    };

                    return (
                      <div className="absolute top-full left-0 right-0 mt-1 bg-paper border border-line rounded-lg shadow-lg z-50 p-1.5">
                        <div className="grid grid-cols-4 gap-1">
                          {[0, 1, 2, 3, 4, 5, 6, 7].map((num) => {
                            const valid = isValidGiven(num, otherSideValue);
                            return (
                              <button
                                key={num}
                                type="button"
                                disabled={!valid}
                                onClick={() => {
                                  const sideIdx = isSide1 ? 'games1' : 'games2';
                                  const otherIdx = isSide1 ? 'games2' : 'games1';

                                  // Compute auto-fill for the opponent (if unambiguous)
                                  const autoFill =
                                    otherSideValue === null ? getAutoFillForOther(num) : null;

                                  setSets((prev) => {
                                    const next = prev.map((x, idx) => {
                                      if (idx !== i) return x;
                                      const updated = { ...x, [sideIdx]: num };
                                      if (autoFill !== null && x[otherIdx] === null) {
                                        updated[otherIdx] = autoFill;
                                      }
                                      return updated;
                                    });
                                    // Check whether the match is decided after this change —
                                    // if so, reset all later sets
                                    let t1 = 0,
                                      t2 = 0;
                                    for (let idx = 0; idx < next.length; idx++) {
                                      const s = next[idx];
                                      if (s.games1 !== null && s.games2 !== null) {
                                        if (s.games1 > s.games2) t1++;
                                        else if (s.games2 > s.games1) t2++;
                                      }
                                      if ((t1 >= 2 || t2 >= 2) && idx < next.length - 1) {
                                        // Reset all sets after this one
                                        for (let j = idx + 1; j < next.length; j++) {
                                          next[j] = { games1: null, games2: null };
                                        }
                                        break;
                                      }
                                    }
                                    return next;
                                  });

                                  // Check whether this set decides the match after the update.
                                  // We simulate the new state locally to know whether to jump ahead.
                                  const newSetsArr = sets.map((x, idx) => {
                                    if (idx !== i) return x;
                                    const updated = { ...x, [sideIdx]: num };
                                    if (autoFill !== null && x[otherIdx] === null)
                                      updated[otherIdx] = autoFill;
                                    return updated;
                                  });
                                  let t1Sets = 0,
                                    t2Sets = 0;
                                  newSetsArr.forEach((s) => {
                                    if (s.games1 !== null && s.games2 !== null) {
                                      if (s.games1 > s.games2) t1Sets++;
                                      else if (s.games2 > s.games1) t2Sets++;
                                    }
                                  });
                                  const matchDecidedAfter = t1Sets >= 2 || t2Sets >= 2;

                                  // Auto-jump:
                                  if (matchDecidedAfter) {
                                    // The match is decided — close the dropdown
                                    setActiveScore(null);
                                  } else if (autoFill !== null) {
                                    // Both auto-filled — jump to the next set
                                    setActiveScore(i < 2 ? `${i + 1}-1` : null);
                                  } else if (otherSideValue === null) {
                                    // Opponent is still empty (not unambiguous) — move focus there
                                    setActiveScore(isSide1 ? `${i}-2` : `${i}-1`);
                                  } else {
                                    // Both are filled — jump to the next set
                                    setActiveScore(i < 2 ? `${i + 1}-1` : null);
                                  }
                                }}
                                className={`font-mono text-sm font-semibold py-1.5 rounded transition-colors ${
                                  valid
                                    ? 'hover:bg-lime-bg/40 cursor-pointer'
                                    : 'text-ink-4 cursor-not-allowed opacity-40'
                                }`}
                              >
                                {num}
                              </button>
                            );
                          })}
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            const sideIdx = isSide1 ? 'games1' : 'games2';
                            setSets((prev) =>
                              prev.map((x, idx) => (idx === i ? { ...x, [sideIdx]: null } : x)),
                            );
                            setActiveScore(null);
                          }}
                          className="w-full text-[10px] text-ink-3 hover:text-rose mt-1 py-1"
                        >
                          Tøm
                        </button>
                      </div>
                    );
                  })()}
              </div>
            );
          })}
        </div>
      </div>

      {/* Date and time */}
      <div className="mb-4">
        <div
          id={ids.when}
          className="font-mono text-[10px] uppercase tracking-wider text-ink-3 mb-2 px-1"
        >
          Dato og klokkeslett
        </div>
        <div role="group" aria-labelledby={ids.when} className="grid grid-cols-2 gap-2">
          <input
            type="date"
            aria-label={t('singleMatches.add.date')}
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="input text-sm"
          />
          <input
            type="time"
            aria-label={t('singleMatches.add.time')}
            value={time}
            onChange={(e) => setTime(e.target.value)}
            className="input text-sm"
          />
        </div>
      </div>

      {/* ELO weight for the match — choose how much it should count (0-200%).
            Uses the same slider and shared descriptions as the tournament setups. */}
      <div className="mb-4 pt-3 border-t border-line">
        <h4 className="font-mono text-[11px] font-semibold uppercase tracking-wider text-ink-3 mb-1.5 flex items-center gap-1.5">
          <TrendingUp size={11} /> ELO-vekt for kampen
        </h4>
        <p className="text-[11px] text-ink-3 mb-3">
          Hvor mye skal denne kampen telle på spillernes ELO? Velg etter hvor seriøs kampen var —
          fra treningskamp (0%) til full best av 3 (200%).
        </p>
        <EloWeightSlider
          value={eloWeightPct / 100}
          onChange={(v) => setEloWeightPct(Math.round(v * 100))}
          recommended={200}
          alwaysUnlocked
        />
      </div>

      {/* Preview */}
      {preview && (
        <div className="mb-4 p-3 rounded-lg bg-lime-bg/30 border border-lime-deep/30">
          <div className="font-mono text-[10px] uppercase tracking-wider text-ink-3 mb-1.5">
            Forhåndsvisning
          </div>
          <div className="grid grid-cols-2 gap-3 text-xs">
            <div>
              <div className="text-ink-2 truncate">
                {playerById[team1.p1Id]?.name} / {playerById[team1.p2Id]?.name}
              </div>
              <div className="font-mono">
                {Math.round(preview.t1EloBefore)}
                <span
                  className={
                    preview.t1Change >= 0
                      ? 'text-lime-deep ml-1 font-semibold'
                      : 'text-rose ml-1 font-semibold'
                  }
                >
                  {preview.t1Change >= 0 ? '+' : ''}
                  {preview.t1Change}
                </span>
              </div>
            </div>
            <div>
              <div className="text-ink-2 truncate">
                {playerById[team2.p1Id]?.name} / {playerById[team2.p2Id]?.name}
              </div>
              <div className="font-mono">
                {Math.round(preview.t2EloBefore)}
                <span
                  className={
                    preview.t2Change >= 0
                      ? 'text-lime-deep ml-1 font-semibold'
                      : 'text-rose ml-1 font-semibold'
                  }
                >
                  {preview.t2Change >= 0 ? '+' : ''}
                  {preview.t2Change}
                </span>
              </div>
            </div>
          </div>
          <div className="mt-1.5 pt-1.5 border-t border-lime-deep/20 text-[11px] text-ink-2">
            Vinner: <strong>{preview.winner === 'team1' ? 'Hjemmelag' : 'Bortelag'}</strong>
            {' · '}Games {preview.t1Games}-{preview.t2Games}
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="flex gap-2 justify-end pt-3 border-t border-line">
        <button
          type="button"
          onClick={onClose}
          className="btn-outline text-sm"
          disabled={submitting}
        >
          Avbryt
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          className="btn-lime text-sm"
          disabled={!canSubmit || submitting}
        >
          {submitting ? 'Lagrer…' : submitLabel || (isEditing ? 'Oppdater kamp' : 'Lagre kamp')}
          <Check size={14} />
        </button>
      </div>
    </Dialog>
  );
}
